import React, { useState, useEffect } from "react";
import styles from "./ApiKeysScreen.module.scss";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Input from "../../../components/input/Input";
import Button from "../../../components/button/Button";
import ResourceList from "../../../components/resourceList/ResourceList";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import { DeleteIcon } from "../../../assets";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  fetchApiKeys,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  createApiKeyResetState,
  deleteApiKeyResetState,
} from "../../../store/actions/admin/apiKeys";
import { Modal } from "@material-ui/core";

const HEADER_VALUES = [
  { label: "NAZIV", key: "name", sortable: true },
  { label: "OPIS", key: "description", sortable: true },
  { label: "ID APLIKACIJE", key: "applicationId", sortable: true },
  { label: "IDENTIFIKATOR", key: "identifier", sortable: true },
  { label: "STATUS", key: "active", sortable: true },
  { label: "KREIRAN", key: "createdAt", sortable: true },
  { label: "POSLEDNJE KORIŠĆENJE", key: "lastUsedAt", sortable: true },
  { label: "AKCIJE", sortable: false },
];

const ApiKeysScreen = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [keyDisplayModalOpen, setKeyDisplayModalOpen] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [apiKeyToDelete, setApiKeyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    applicationId: "",
  });

  const apiKeys = useSelector((state) => state.apiKeys.apiKeys);
  const createApiKeyInfo = useSelector((state) => state.apiKeys.createApiKey);
  const deleteApiKeyInfo = useSelector((state) => state.apiKeys.deleteApiKey);

  useEffect(() => {
    dispatch(fetchApiKeys(1, 10, "", "id", "DESC"));
  }, [dispatch]);

  useEffect(() => {
    if (createApiKeyInfo.success && createApiKeyInfo.createdKey) {
      setKeyDisplayModalOpen(true);
      setCreateModalOpen(false);
      setFormData({ name: "", description: "", applicationId: "" });
      dispatch(fetchApiKeys(apiKeys.page, apiKeys.perPage, searchTerm, "id", "DESC"));
    }
  }, [createApiKeyInfo.success]);

  useEffect(() => {
    if (createApiKeyInfo.error) {
      toast.error("Greška prilikom kreiranja API ključa");
      dispatch(createApiKeyResetState());
    }
  }, [createApiKeyInfo.error]);

  useEffect(() => {
    if (deleteApiKeyInfo.success) {
      toast.success("API ključ uspešno obrisan");
      setDeleteModalOpen(false);
      setApiKeyToDelete(null);
      dispatch(deleteApiKeyResetState());
      dispatch(fetchApiKeys(apiKeys.page, apiKeys.perPage, searchTerm, "id", "DESC"));
    }
  }, [deleteApiKeyInfo.success]);

  useEffect(() => {
    if (deleteApiKeyInfo.error) {
      toast.error("Greška prilikom brisanja API ključa");
      dispatch(deleteApiKeyResetState());
    }
  }, [deleteApiKeyInfo.error]);

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error("Naziv je obavezan");
      return;
    }
    dispatch(createApiKey(formData.name, formData.description || null, formData.applicationId || null));
  };

  const handleToggleActive = async (apiKey) => {
    try {
      await dispatch(updateApiKey(apiKey.id, apiKey.name, apiKey.description, apiKey.applicationId, !apiKey.active));
      toast.success(`API ključ ${!apiKey.active ? "aktiviran" : "deaktiviran"}`);
      dispatch(fetchApiKeys(apiKeys.page, apiKeys.perPage, searchTerm, "id", "DESC"));
    } catch (error) {
      toast.error("Greška prilikom ažuriranja API ključa");
    }
  };

  const handleDeleteClick = (key) => {
    setApiKeyToDelete(key);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (apiKeyToDelete) {
      dispatch(deleteApiKey(apiKeyToDelete.id));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Kopirano u clipboard");
    });
  };

  const onLoadPage = (page) => {
    dispatch(fetchApiKeys(page, apiKeys.perPage, searchTerm, "id", "DESC"));
  };

  const onSort = (sortBy, order) => {
    dispatch(fetchApiKeys(apiKeys.page, apiKeys.perPage, searchTerm, sortBy, order));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    dispatch(fetchApiKeys(1, apiKeys.perPage, value, "id", "DESC"));
  };

  const renderApiKeyRow = (key, index) => {
    const createdAt = key.createdAt ? moment(key.createdAt).format("ddd DD. MMM, HH:mm:ss") : "-";
    const lastUsedAt = key.lastUsedAt ? moment(key.lastUsedAt).format("ddd DD. MMM, HH:mm:ss") : "-";

    if (isMobile) {
      return (
        <div className={styles.listCard} key={key.id}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Naziv</p>
            <p className={styles.rowCardValue}>{key.name}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Opis</p>
            <p className={styles.rowCardValue}>{key.description || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>ID Aplikacije</p>
            <p className={styles.rowCardValue}>{key.applicationId || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Identifikator</p>
            <p className={styles.rowCardValue}>{key.identifier || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Status</p>
            <span
              className={classNames(
                styles.statusBadge,
                key.active ? styles.active : styles.inactive
              )}
            >
              {key.active ? "Aktivan" : "Neaktivan"}
            </span>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Kreiran</p>
            <p className={styles.rowCardValue}>{createdAt}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Poslednje korišćenje</p>
            <p className={styles.rowCardValue}>{lastUsedAt}</p>
          </div>
          <div className={styles.rowCard}>
            <div className={styles.actions}>
              <Button
                name={key.active ? "Deaktiviraj" : "Aktiviraj"}
                onClick={() => handleToggleActive(key)}
                className={styles.toggleButton}
              />
              <img
                src={DeleteIcon}
                alt="delete"
                className={styles.deleteIcon}
                onClick={() => handleDeleteClick(key)}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={key.id}
      >
        <div className={styles.cell}>
          <p>{key.name}</p>
        </div>
        <div className={styles.cell}>
          <p>{key.description || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{key.applicationId || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{key.identifier || "-"}</p>
        </div>
        <div className={styles.cell}>
          <span
            className={classNames(
              styles.statusBadge,
              key.active ? styles.active : styles.inactive
            )}
          >
            {key.active ? "Aktivan" : "Neaktivan"}
          </span>
        </div>
        <div className={styles.cell}>
          <p>{createdAt}</p>
        </div>
        <div className={styles.cell}>
          <p>{lastUsedAt}</p>
        </div>
        <div className={styles.cell}>
          <div className={styles.actions}>
            <Button
              name={key.active ? "Deaktiviraj" : "Aktiviraj"}
              onClick={() => handleToggleActive(key)}
              className={styles.toggleButton}
            />
            <img
              src={DeleteIcon}
              alt="delete"
              className={styles.deleteIcon}
              onClick={() => handleDeleteClick(key)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <AreYouSureModal
        modalOpen={deleteModalOpen}
        closeModal={() => {
          setDeleteModalOpen(false);
          setApiKeyToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Brisanje API ključa"
        message={
          apiKeyToDelete && (
            <div>
              <p>Da li ste sigurni da želite da obrišete API ključ:</p>
              <p><strong>{apiKeyToDelete.name}</strong></p>
            </div>
          )
        }
      />

      {/* Create Modal */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <div className={styles.modalWrap}>
          <div className={styles.modalInner}>
            <h2 className={styles.modalTitle}>Dodavanje ključa</h2>
            <div className={styles.modalForm}>
              <div className={styles.inputWrapper}>
                <p>Naziv *</p>
                <Input
                  onChange={(value) => setFormData({ ...formData, name: value })}
                  value={formData.name}
                />
              </div>
              <div className={styles.inputWrapper}>
                <p>Opis *</p>
                <Input
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  value={formData.description}
                />
              </div>
              <div className={styles.inputWrapper}>
                <p>Application ID *</p>
                <Input
                  onChange={(value) => setFormData({ ...formData, applicationId: value })}
                  value={formData.applicationId}
                />
              </div>
              <div className={styles.modalButtons}>
                <p className={styles.cancel} onClick={() => setCreateModalOpen(false)}>
                  Otkaži
                </p>
                <Button
                  name="Nastavak"
                  onClick={handleCreate}
                  disabled={createApiKeyInfo.attempt}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Key Display Modal */}
      <Modal open={keyDisplayModalOpen} onClose={() => {}}>
        <div className={styles.modalWrap}>
          <div className={styles.modalInner}>
            <h2 className={styles.modalTitle}>APP Integration Ključ</h2>
            <div className={styles.keyDisplayContent}>
              <p className={styles.successMessage}>API Ključ uspešno kreiran!</p>
              <p className={styles.warningMessage}>
                Ovaj API ključ sačuvajte pre nego što zatvorite prozor. On više neće moći da se prikaže.
              </p>
              
              {createApiKeyInfo.createdKey && (
                <>
                  <div className={styles.keyField}>
                    <label>key</label>
                    <div className={styles.keyValueWrapper}>
                      <input
                        type="text"
                        readOnly
                        value={createApiKeyInfo.createdKey.key}
                        className={styles.keyValue}
                      />
                      <Button
                        name="Kopiraj"
                        onClick={() => copyToClipboard(createApiKeyInfo.createdKey.key)}
                        className={styles.copyButton}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.keyField}>
                    <label>identifier</label>
                    <div className={styles.keyValueWrapper}>
                      <input
                        type="text"
                        readOnly
                        value={createApiKeyInfo.createdKey.identifier}
                        className={styles.keyValue}
                      />
                      <Button
                        name="Kopiraj"
                        onClick={() => copyToClipboard(createApiKeyInfo.createdKey.identifier)}
                        className={styles.copyButton}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      id="keySaved"
                      checked={keySaved}
                      onChange={(e) => setKeySaved(e.target.checked)}
                    />
                    <label htmlFor="keySaved">Sačuvao/la sam ključ na sigurno mesto</label>
                  </div>
                  
                  <div className={styles.modalButtons}>
                    <Button
                      name="Zatvori"
                      onClick={() => {
                        if (keySaved) {
                          setKeyDisplayModalOpen(false);
                          setKeySaved(false);
                          dispatch(createApiKeyResetState());
                        } else {
                          toast.warning("Molimo potvrdite da ste sačuvali ključ");
                        }
                      }}
                      className={styles.closeButton}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>API Ključevi</h1>
        </div>

        <div className={styles.content}>
          <div className={classNames(styles.section)}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>Pregled</p>
              <div className={styles.headerActions}>
                <div className={styles.searchWrapper}>
                  <Input
                    placeholder="Pretraži API ključeve..."
                    onChange={handleSearch}
                    value={searchTerm}
                  />
                </div>
                <Button
                  name="Dodaj novi"
                  onClick={() => setCreateModalOpen(true)}
                  className={styles.addButton}
                />
              </div>
            </div>
            <ResourceList
              resource={apiKeys}
              onSort={onSort}
              onLoadPage={onLoadPage}
              headerValues={HEADER_VALUES}
              rowRender={renderApiKeyRow}
              showLoader={true}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApiKeysScreen;

